import { Colors } from "../../../utils/colors";
import StandardBorderedContainer from "../../../components/Containers/StandardBorderedContainer";
import { Icon } from "@iconify/react";
import CreatorModePanelWrapper from "../../../components/CreatorDashboard/CreatorModePanelWrapper";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import RectangleButtonCreatorPanel from "../../../components/Buttons/RectangleButtonCreatorPanel";
import { HorizontalDivider } from "../../../components/Dividers/HorizontalDivider";
import { CopyableLinkDisplay } from "../../../components/Links/CopyCustomLinkify";
import RectangleButton from "../../../components/Buttons/RectangleButton";
import { MARKIT_DOCS, MARKITAI } from "@markit/common.utils";
import { NotificationType } from "@markit/common.types";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import ReferralHistoryModal from "../../../components/CreatorDashboard/ReferralHistoryModal";

const ReferralProgramPanel = () => {
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [referralHistoryModal, setReferralHistoryModal] = useState(false);

  const styles = {
    bodyContainer: {
      backgroundColor: Colors.WHITE1,
      paddingTop: 40,
      paddingBottom: 100,
      height: "100%",
    },
    boxContainer: {
      gap: 10,
      width: 544,
      margin: "auto",
    },
    sectionContainer: {
      paddingBlock: 14,
      backgroundColor: Colors.WHITE,
      width: 544,
    },
  };

  const referralCodeCopyMessage = useMemo(
    () =>
      `Enter my code ${accountData.username.toUpperCase()} at checkout for a Markit+ Pro/Business subscription.\n${MARKITAI}/welcome?referral=${accountData.username.toUpperCase()}`,
    [accountData.username]
  );

  const copyReferralLink = useCallback(async () => {
    navigator.clipboard.writeText(referralCodeCopyMessage).then(() => {
      showNotificationBanner(dispatch, "Copied!", NotificationType.AFFIRMATIVE);
    });
  }, [dispatch, referralCodeCopyMessage]);

  const renderSectionItem = useCallback(
    (
      iconName: string,
      iconColor: string,
      title: string,
      description: ReactNode
    ) => (
      <div className="AlignedRow" style={{ gap: 20, paddingInline: 14 }}>
        <div>
          <Icon icon={iconName} height={30} style={{ color: iconColor }} />
        </div>
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <span className="sectionTitle">{title}</span>
          {description}
        </div>
      </div>
    ),
    []
  );

  return (
    <CreatorModePanelWrapper
      title={"Markit Referral Program"}
      headerRight={
        <RectangleButtonCreatorPanel
          title="Copy Referral Code"
          iconName="ion:copy"
          onPress={copyReferralLink}
        />
      }
    >
      <div style={styles.bodyContainer}>
        <div className="ColumnNormal" style={{ gap: 30, alignItems: "center" }}>
          <div className="ColumnNormal" style={{ ...styles.boxContainer }}>
            <span className="sectionTitle" style={{ textAlign: "center" }}>
              Refer a friend and earn up to $300
              <br />
              on every eligible referral!
            </span>
          </div>
          <StandardBorderedContainer
            containerStyles={{
              ...styles.sectionContainer,
              display: "flex",
              gap: 14,
            }}
            className="ColumnNormal"
          >
            <div className="ColumnNormal" style={{ gap: 4, paddingInline: 14 }}>
              <span className="bodyMedium">Share your referral code</span>
              <span className="smallBodySubtext">
                Referee must complete all steps in referral rules to be eligible
                for rewards and
                <br />
                <span className="boldBlackText">
                  must apply your referral code at checkout for you to receive
                  rewards.
                </span>
              </span>
            </div>
            <CopyableLinkDisplay
              copyText={referralCodeCopyMessage}
              multiLineText
              containerStyles={{
                padding: 14,
                backgroundColor: Colors.GRAY6,
                border: `0.5px solid ${Colors.GRAY1}`,
                marginInline: 14,
                fontWeight: 500,
              }}
            />
            <HorizontalDivider />
            <div
              className="AlignedRowSpacedSelect"
              style={{
                paddingInline: 14,
                pointerEvents:
                  accountData.profileReferrals.length > 0 ? "all" : "none",
              }}
              onClick={() => setReferralHistoryModal(true)}
            >
              <div className="ColumnNormal" style={{ gap: 4 }}>
                <span className="bodyMedium">
                  Referral History ({accountData.profileReferrals.length})
                </span>
                <span className="smallBodySubtext">
                  View the users you have referred to Markit+
                </span>
              </div>
              {accountData.profileReferrals.length > 0 ? (
                <Icon
                  icon="ion:chevron-forward"
                  height={20}
                  style={{ color: Colors.GRAY1 }}
                />
              ) : null}
            </div>
          </StandardBorderedContainer>
          <StandardBorderedContainer containerStyles={styles.sectionContainer}>
            <span className="sectionTitle" style={{ paddingInline: 14 }}>
              Referral Rules
            </span>
            <HorizontalDivider altMargin={14} />
            {renderSectionItem(
              "ion:checkmark-circle",
              Colors.BLACK,
              "Referee/Invitee Eligibility",
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <span className="smallBodySubtext">
                  The referred user must purchase a Markit+ Pro or Business
                  subscription to be valid.{" "}
                  <span className="boldBlackText">
                    They must apply your referral code at checkout for you to
                    receive rewards.
                  </span>
                </span>
                <span className="bodySmallMedium">
                  Business Plan Referral:{" "}
                  <span className="smallBodySubtext">
                    Referee must use at least 10,000 text credits and have an
                    active Markit+ business plan subscription.
                  </span>
                </span>
                <span className="bodySmallMedium">
                  Pro Plan Referral:{" "}
                  <span className="smallBodySubtext">
                    Referee must use at least 2,500 text credits and have an
                    active Markit+ pro plan subscription.
                  </span>
                </span>
              </div>
            )}
            <HorizontalDivider altMargin={14} />
            {renderSectionItem(
              "ion:gift",
              Colors.GREEN2,
              "Referral Rewards",
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <span className="bodySmallMedium">
                  Business Plan Referral:{" "}
                  <span className="smallBodySubtext">
                    Referrer/Inviter will receive{" "}
                    <span className="boldBlackText">$300</span> per eligible
                    Business plan referral.
                  </span>
                </span>
                <span className="bodySmallMedium">
                  Pro Plan Referral:{" "}
                  <span className="smallBodySubtext">
                    Referrer/Inviter will receive{" "}
                    <span className="boldBlackText">$60</span> per eligible Pro
                    plan referral.
                  </span>
                </span>
                <span className="smallBodySubtext">
                  Markit will send payouts via Zelle or Venmo.
                  <br />
                  There is no limit to how many people you can refer to use
                  Markit+.
                </span>
              </div>
            )}
          </StandardBorderedContainer>
          <div style={{ margin: "auto" }}>
            <RectangleButton
              buttonLabel="Referral Terms"
              onPress={() => window.open(MARKIT_DOCS + "/referralterms")}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              altPaddingHorz={16}
              altPaddingVert={12}
              altBorderRadius={22}
              iconRight={
                <Icon
                  icon="ion:chevron-forward"
                  height={14}
                  style={{ color: Colors.WHITE }}
                />
              }
            />
          </div>
        </div>
      </div>
      {referralHistoryModal ? (
        <ReferralHistoryModal
          isVisible={referralHistoryModal}
          setIsVisible={setReferralHistoryModal}
        />
      ) : null}
    </CreatorModePanelWrapper>
  );
};

export default ReferralProgramPanel;
